import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  billingOf,
  describeDue,
  dueBucket,
  formatRupees,
  localDay,
  PAYMENT_STATE_LABELS,
  REPORT_STATUSES,
  type ReportStatus,
} from '@quickreport/domain';
import { Badge, Button, Field, Input, Select } from '@quickreport/ui';
import {
  type ReportListItem,
  type ReportSort,
  reportCountQuery,
  reportListQuery,
} from '@quickreport/workspace';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { CloudOff, Lock, Plus, Search, SlidersHorizontal } from 'lucide-react';
import { useDeferredValue, useEffect, useState } from 'react';
import { DEFAULT_PAGE, Pagination } from '../../components/Pagination';
import { useWorkspace } from '../../lib/workspace';
import { STATUS_LABEL, STATUS_TONE } from './context';
import { PAY_TONE } from './PaymentPanel';

export function ReportsPage() {
  const ws = useWorkspace();
  const navigate = useNavigate();
  const search = useSearch({ from: '/reports' });
  const [q, setQ] = useState(search.q ?? '');
  const dq = useDeferredValue(q);
  const [status, setStatus] = useState<ReportStatus | ''>('');
  const [sort, setSort] = useState<ReportSort>('updated');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sync, setSync] = useState<'' | 'waiting' | 'server'>('');
  const [qr, setQr] = useState<'' | 'with' | 'without'>('');
  const [pay, setPay] = useState<'' | 'unpaid' | 'part' | 'paid'>('');
  const showMoney = ws.can('view_billing');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const filters = {
    q: dq,
    sort,
    order,
    ...(status ? { statuses: [status] } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(sync ? { sync } : {}),
    ...(qr ? { qr } : {}),
    ...(showMoney && pay ? { pay } : {}),
  };
  const { sql, params } = reportListQuery(ws.orgId, { ...filters, page, pageSize });
  const count = reportCountQuery(ws.orgId, filters);
  const res = useLiveQuery<ReportListItem>(sql, params);
  const totalRes = useLiveQuery<{ n: number }>(count.sql, count.params);
  const total = totalRes?.rows[0]?.n ?? 0;
  const rows = res?.rows ?? [];
  const anyFilter = Boolean(
    status || from || to || sync || qr || pay || q || sort !== 'updated' || order !== 'desc',
  );

  // A different search or filter starts again from the first page; a page that no longer exists
  // (after deleting or filtering) falls back to the last one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: these are the changes that restart the list
  useEffect(() => setPage(1), [dq, status, sort, order, from, to, sync, qr, pay, pageSize]);
  // If the list gets shorter (a filter, a deletion), stay within the pages that still exist. Using
  // the latest page number here keeps this from undoing a reset to page 1.
  useEffect(() => {
    const last = Math.max(1, Math.ceil(total / pageSize));
    setPage((p) => Math.min(p, last));
  }, [total, pageSize]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving the page clears the picked rows
  useEffect(
    () => setSelected(new Set()),
    [page, dq, status, sort, order, from, to, sync, qr, pay, pageSize],
  );

  const activeFilters =
    [status, from, to, sync, qr, showMoney ? pay : ''].filter(Boolean).length +
    (sort !== 'updated' || order !== 'desc' ? 1 : 0);

  function clearFilters() {
    setQ('');
    setStatus('');
    setSort('updated');
    setOrder('desc');
    setFrom('');
    setTo('');
    setSync('');
    setQr('');
    setPay('');
  }
  const canRegister = ws.can('register_visit');

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <div className="flex h-full flex-col md:flex-row">
      <div className="min-w-0 flex-1 overflow-auto p-3 md:p-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="mr-auto text-xl font-semibold">Reports</h1>
          {canRegister ? (
            <Link
              to="/reports/new"
              className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-navy px-3 font-medium text-white shadow-sm hover:bg-navy-hover max-md:hidden"
            >
              <Plus size={16} /> New report
            </Link>
          ) : null}
        </div>

        <div className="mt-3 flex items-center gap-2 md:mt-4 md:flex-wrap md:gap-3">
          <div className="relative min-w-0 flex-1 md:min-w-64">
            <Search size={16} className="pointer-events-none absolute top-2 left-2.5 text-slate" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search patient, mobile, lab no. or test"
              aria-label="Search reports"
              className="h-10 pl-8 md:h-9"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters((v) => !v)}
            aria-expanded={showFilters}
            className="relative inline-flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-line bg-paper px-3 text-[13px] font-medium text-navy md:hidden"
          >
            <SlidersHorizontal size={16} /> Filters
            {activeFilters > 0 ? (
              <span className="grid h-5 min-w-5 place-items-center rounded-full bg-accent px-1 text-[11px] font-semibold text-white">
                {activeFilters}
              </span>
            ) : null}
          </button>
        </div>

        <div
          className={clsx(
            'mt-3 gap-3 md:flex md:flex-wrap md:items-end',
            showFilters ? 'grid grid-cols-2 rounded-xl border border-line bg-paper p-3' : 'hidden',
            'max-md:[&_input]:w-full max-md:[&_select]:w-full max-md:[&_select]:h-10 max-md:[&_input]:h-10',
          )}
        >
          <Field label="Status">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as ReportStatus | '')}
              className="h-9 w-44"
              aria-label="Filter by status"
            >
              <option value="">Any status</option>
              {REPORT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Sort by">
            <Select
              value={sort}
              onChange={(e) => setSort(e.target.value as ReportSort)}
              className="h-9 w-44"
              aria-label="Sort by"
            >
              <option value="updated">Last updated</option>
              <option value="collected">Sample collected</option>
              <option value="received">Sample received</option>
            </Select>
          </Field>
          <Field label="Order">
            <Select
              value={order}
              onChange={(e) => setOrder(e.target.value as 'asc' | 'desc')}
              className="h-9 w-36"
              aria-label="Order"
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </Select>
          </Field>
          <Field label={`From (${SORT_NAME[sort]})`}>
            <Input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
              className="h-9 w-40"
              aria-label="From date"
            />
          </Field>
          <Field label="To">
            <Input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
              className="h-9 w-40"
              aria-label="To date"
            />
          </Field>
          <Field label="Upload">
            <Select
              value={sync}
              onChange={(e) => setSync(e.target.value as '' | 'waiting' | 'server')}
              className="h-9 w-44"
              aria-label="Upload state"
            >
              <option value="">Any</option>
              <option value="waiting">Waiting to upload</option>
              <option value="server">Already on the server</option>
            </Select>
          </Field>
          <Field label="QR code">
            <Select
              value={qr}
              onChange={(e) => setQr(e.target.value as '' | 'with' | 'without')}
              className="h-9 w-36"
              aria-label="QR code"
            >
              <option value="">Any</option>
              <option value="with">Has a QR</option>
              <option value="without">No QR yet</option>
            </Select>
          </Field>
          {showMoney ? (
            <Field label="Payment">
              <Select
                value={pay}
                onChange={(e) => setPay(e.target.value as '' | 'unpaid' | 'part' | 'paid')}
                className="h-9 w-36"
                aria-label="Payment state"
              >
                <option value="">Any</option>
                <option value="unpaid">Unpaid</option>
                <option value="part">Part paid</option>
                <option value="paid">Paid</option>
              </Select>
            </Field>
          ) : null}
          {anyFilter ? (
            <Button size="sm" className="mb-0.5 max-md:col-span-2" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : null}
        </div>

        {selected.size > 0 ? (
          <div
            className="mt-3 flex flex-wrap items-center gap-3 rounded-lg bg-brand-soft px-4 py-2 text-[13px]"
            role="status"
          >
            <span className="font-medium">{selected.size} selected</span>
            <button
              type="button"
              className="ml-auto text-brand hover:underline"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </button>
          </div>
        ) : null}

        <ul className="mt-3 grid gap-2 md:hidden" aria-label="Reports">
          {rows.map((r) => (
            <li
              key={r.id}
              className={clsx(
                'flex gap-3 rounded-xl border border-line bg-paper p-3 active:bg-brand-soft/50',
                selected.has(r.id) && 'bg-brand-soft/40',
              )}
            >
              <input
                type="checkbox"
                aria-label={`Select ${r.name}`}
                className="mt-0.5 h-5 w-5 shrink-0 accent-brand"
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
              />
              <Link
                to="/reports/$reportId"
                params={{ reportId: r.id }}
                className="min-w-0 flex-1 text-ink"
              >
                <span className="flex items-start gap-2">
                  <span className="min-w-0 flex-1 leading-snug font-medium">
                    {r.name}
                    {r.sensitive ? (
                      <Lock size={12} className="ml-1.5 inline text-slate" aria-label="Sensitive" />
                    ) : null}
                  </span>
                  <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>
                    {STATUS_LABEL[r.status] ?? r.status}
                  </Badge>
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px] text-slate">
                  <span className="font-mono">{r.lab_no}</span>
                  <span>Collected {when(r.collected_at)}</span>
                </span>
                <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <DueBadge
                    quiet
                    day={r.due_date}
                    done={Boolean(r.due_done_at)}
                    status={r.status}
                  />
                  {showMoney ? <PayBadge quiet due={r.due_paise} paid={r.paid_paise} /> : null}
                  {(r.status === 'final' || r.status === 'partial') && !r.verify_code ? (
                    <Badge>No QR yet</Badge>
                  ) : null}
                  {r.pending ? (
                    <span className="inline-flex items-center gap-1 text-[12px] text-warn">
                      <CloudOff size={12} /> Not synced
                    </span>
                  ) : null}
                </span>
              </Link>
            </li>
          ))}
          {res && rows.length === 0 ? (
            <li className="rounded-xl border border-dashed border-line bg-paper px-4 py-10 text-center text-slate">
              {anyFilter
                ? 'No report matches. Try clearing the search or filters.'
                : canRegister
                  ? 'No reports yet. Tap the + button to register a patient and start one.'
                  : 'No reports yet.'}
            </li>
          ) : null}
        </ul>

        <div className="mt-4 hidden overflow-hidden rounded-lg border border-line bg-paper md:block">
          <table className="w-full text-left">
            <thead className="bg-canvas text-[12px] tracking-wide text-slate uppercase">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    className="accent-brand"
                    checked={rows.length > 0 && selected.size === rows.length}
                    onChange={(e) =>
                      setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                    }
                  />
                </th>
                <th className="px-3 py-2 font-medium">Lab no.</th>
                <th className="px-3 py-2 font-medium">Report</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Collected</th>
                <th className="px-3 py-2 font-medium">Received</th>
                <th className="px-3 py-2 font-medium">Due</th>
                {showMoney ? <th className="px-3 py-2 font-medium">Payment</th> : null}
                <th className="px-3 py-2 text-right font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className={clsx(
                    'cursor-pointer border-t border-line hover:bg-brand-soft/50',
                    selected.has(r.id) && 'bg-brand-soft/40',
                  )}
                  onClick={() =>
                    void navigate({ to: '/reports/$reportId', params: { reportId: r.id } })
                  }
                >
                  <td
                    className="px-3 py-2.5"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${r.name}`}
                      className="accent-brand"
                      checked={selected.has(r.id)}
                      onChange={() => toggle(r.id)}
                    />
                  </td>
                  <td className="px-3 py-2.5 font-mono text-[13px]">{r.lab_no}</td>
                  <td className="px-3 py-2.5">
                    <Link
                      to="/reports/$reportId"
                      params={{ reportId: r.id }}
                      className="font-medium hover:text-brand"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {r.name}
                    </Link>
                    {r.sensitive ? (
                      <Lock size={12} className="ml-1.5 inline text-slate" aria-label="Sensitive" />
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>
                        {STATUS_LABEL[r.status] ?? r.status}
                      </Badge>
                      {(r.status === 'final' || r.status === 'partial') && !r.verify_code ? (
                        <Badge>No QR yet</Badge>
                      ) : null}
                      {r.pending ? (
                        <span
                          className="inline-flex items-center gap-1 text-[12px] text-warn"
                          title="Not uploaded yet"
                        >
                          <CloudOff size={12} /> Not synced
                        </span>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-[13px] text-slate">{when(r.collected_at)}</td>
                  <td className="px-3 py-2.5 text-[13px] text-slate">{when(r.received_at)}</td>
                  <td className="px-3 py-2.5">
                    <DueBadge day={r.due_date} done={Boolean(r.due_done_at)} status={r.status} />
                  </td>
                  {showMoney ? (
                    <td className="px-3 py-2.5">
                      <PayBadge due={r.due_paise} paid={r.paid_paise} />
                    </td>
                  ) : null}
                  <td className="px-3 py-2.5 text-right text-[13px] text-slate">
                    {when(r.updated_at)}
                  </td>
                </tr>
              ))}
              {res && rows.length === 0 ? (
                <tr>
                  <td colSpan={showMoney ? 9 : 8} className="px-4 py-14 text-center text-slate">
                    {anyFilter
                      ? 'No report matches. Try clearing the search or filters.'
                      : canRegister
                        ? 'No reports yet. Click “New report” to register a patient and start one.'
                        : 'No reports yet.'}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
          noun="reports"
        />
      </div>
    </div>
  );
}

const SORT_NAME: Record<ReportSort, string> = {
  updated: 'last updated',
  collected: 'sample collected',
  received: 'sample received',
};

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Paid, part paid or unpaid, with the balance when something is still to pay. */
function PayBadge({ due, paid, quiet = false }: { due: number; paid: number; quiet?: boolean }) {
  const b = billingOf(due, 0, paid);
  if (b.state === 'free') return quiet ? null : <span className="text-[13px] text-slate">—</span>;
  return (
    <Badge tone={PAY_TONE[b.state]}>
      {PAYMENT_STATE_LABELS[b.state]}
      {b.balancePaise > 0 ? ` · ${formatRupees(b.balancePaise)} due` : ''}
    </Badge>
  );
}

/** When a report is promised: red when late, amber today, quiet once it is signed or marked complete. */
function DueBadge({
  day,
  done,
  status,
  quiet = false,
}: {
  day: string | null;
  done: boolean;
  status: string;
  quiet?: boolean;
}) {
  if (!day || done || status === 'final' || status === 'revoked')
    return quiet ? null : <span className="text-[13px] text-slate">—</span>;
  const today = localDay();
  const b = dueBucket(day, today);
  return (
    <Badge tone={b === 'overdue' ? 'flag' : b === 'today' ? 'brand' : 'neutral'}>
      {b === 'overdue' ? 'Overdue' : b === 'today' ? 'Due today' : describeDue(day, today)}
    </Badge>
  );
}
